import { useCallback } from 'react';

import {
  GlobalCompanyTabCompanyType,
  GlobalCompanyTabName,
  GlobalCompanyTabRoleUID
} from '../../../../../globalCompanyTabsTypes';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';

import { useCreateGlobalCompanyTab } from '../../../../../hooks/useCreateGlobalCompanyTab';
import {
  CREATE_GLOBAL_COMPANY_TAB_QUERY,
  CreateGlobalCompanyTabQueryResponse
} from '../../../../../queries/createGlobalCompanyTab.query';

import { GlobalCompanyTabsCache } from '../../../../../GlobalCompanyTabsCache';

export interface CreateGlobalCompanyTabFormData {
  name: GlobalCompanyTabName;
  roleIds: GlobalCompanyTabRoleUID[];
}

const DEFAULT_VALUES: CreateGlobalCompanyTabFormData = {
  name: '',
  roleIds: []
};

interface UseCreateGlobalCompanyTabFormProps {
  companyType: GlobalCompanyTabCompanyType;
  hideModal: () => void;
}

function useCreateGlobalCompanyTabForm({
  companyType,
  hideModal
}: UseCreateGlobalCompanyTabFormProps) {
  const { control, errors, handleSubmitReactHookForm, register } =
    useReactHookForm<CreateGlobalCompanyTabFormData>({
      defaultValues: DEFAULT_VALUES,
      isModalForm: true
    });

  const {
    createGlobalCompanyTab,
    createGlobalCompanyTabLoading,
    createGlobalCompanyTabErrorMessage
  } = useCreateGlobalCompanyTab<CreateGlobalCompanyTabQueryResponse>({
    query: CREATE_GLOBAL_COMPANY_TAB_QUERY,
    cacheKeys: [GlobalCompanyTabsCache.indexCacheKey()]
  });

  const registerName = register('name', { required: true });

  const handleSubmit = useCallback<() => void>(() => {
    handleSubmitReactHookForm({
      onSubmit: async (data: CreateGlobalCompanyTabFormData) =>
        createGlobalCompanyTab({
          companyType,
          name: data.name.trim(),
          roleUids: data.roleIds
        }).then((response) => {
          if (response?.createGlobalCompanyTab?.status === 'success') {
            hideModal();
          }
        }),
      dirtyFieldsOnly: false
    })();
  }, [
    handleSubmitReactHookForm,
    createGlobalCompanyTab,
    companyType,
    hideModal
  ]);

  return {
    control,
    errors,
    registerName,
    handleSubmit,
    createGlobalCompanyTabLoading,
    createGlobalCompanyTabErrorMessage
  };
}

export default useCreateGlobalCompanyTabForm;
